import { aiApi } from "api";
import { create_UUID, getTranslation, buildShareUrl } from "common";
import { useCallback, useEffect, useRef, useState } from "react";
import useTranslations from "./useTranslations";
import useSelectedOrganization from "./useSelectedOrganization";
import { useSnackbar, useUser } from "hooks";
import axios from "axios";

const checkIfParsable = (m: string) => {
  const numTick = Array.from(m.matchAll(/```/g)).length;
  if (!numTick) {
    return true;
  }
  return numTick % 2 === 0;
};

const titleCase = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/**
 * Extract a topic-like title from a message (e.g. "Create a process for invoice approval").
 * Takes the first sentence, capitalizes the first 4-5 words in Title Case, and clamps
 * the result to a reasonable length. Keeps stopwords so the title reads naturally.
 */
const extractTopicTitle = (message: string, fallback: string, maxLen = 40): string => {
  if (!message || !message.trim()) return fallback;

  // Take the first sentence — split on sentence terminators and newlines
  const firstSentence = message.split(/[.!?\n]/)[0].trim();
  if (!firstSentence) return fallback;

  // Capitalize the first 4-5 words in Title Case
  const picked = firstSentence.split(/\s+/).filter(Boolean).slice(0, 5);
  const title = picked.map(titleCase).join(" ");

  return title.length > maxLen ? title.substring(0, maxLen).trim() + "…" : title;
};

export type AiMessage = {
  uuid: string;
  isAi: boolean;
  message: string;
  isLoading: boolean;
  hidden?: boolean;
  parsing?: boolean;
  isError?: boolean;
  prompt?: string;
  extra?: any;
};

export type ChatSession = {
  id: string;
  title: string;
  timestamp: number;
  messages?: AiMessage[];
  url: string;
  idSession?: string;
};

/**
 * Backend endpoints (`/ai/sessions/...` and `/ai/sessions/.../search`) may
 * return the session list either as a bare array or wrapped in an object
 * (`{ data: [...] }`, `{ results: [...] }`, etc.). This helper unwraps the
 * array in a forgiving way and logs the wrapper key it found so we can
 * detect future backend changes quickly.
 */
const extractArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[AiDrawer] session payload shape:", {
        wrapper: "array",
        count: payload.length,
      });
    }
    return payload;
  }
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    for (const key of ["data", "results", "items", "sessions"]) {
      const candidate = obj[key];
      if (Array.isArray(candidate)) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[AiDrawer] session payload shape:", {
            wrapper: key,
            count: candidate.length,
          });
        }
        return candidate;
      }
    }
  }
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[AiDrawer] session payload shape:", { wrapper: "none", count: 0 });
  }
  return [];
};

/**
 * Map a single backend session record to the ChatSession shape the UI
 * expects. Returns null when the record is missing a usable id, so the
 * caller can filter it out and keep React keys unique.
 *
 * Accepts multiple key variants (`id_session` / `id` / `sessionId`,
 * `updated_at` / `created_at`, etc.) so the client stays compatible if
 * the backend ever normalizes its shape.
 */
const normalizeSession = (raw: any): ChatSession | null => {
  if (!raw || typeof raw !== "object") return null;

  const idValue = raw.id_session ?? raw.idSession ?? raw.id ?? raw.sessionId;
  const id = idValue !== undefined && idValue !== null ? String(idValue) : "";
  if (!id) return null;

  const idSession = id; // `id_session` IS the session id used by load/delete/rename

  // Title comes exclusively from the backend. We deliberately do NOT
  // fall back to `first_question` here: the backend is the only source
  // of truth for the auto-generated title, and `searchSessions` plus
  // the session-list useEffect will replace this value with the
  // cached one (or a placeholder) as soon as the proper title is
  // available.
  const title = String(raw.title ?? "");

  const dateSource = raw.updated_at ?? raw.created_at;
  let timestamp = Date.now();
  if (typeof dateSource === "string" || typeof dateSource === "number") {
    const parsed = new Date(dateSource).getTime();
    if (!Number.isNaN(parsed)) timestamp = parsed;
  }

  // Map backend `{ question, answer }` pairs to the client's AiMessage pairs.
  let messages: AiMessage[] | undefined;
  if (Array.isArray(raw.messages)) {
    const mapped = raw.messages.flatMap((m: any, idx: number) => {
      const entry = m && typeof m === "object" ? m : {};
      const entryId = entry.id ?? idx;
      const result: AiMessage[] = [];
      if (typeof entry.question === "string" && entry.question.length > 0) {
        result.push({
          uuid: `${id}-${entryId}-q`,
          isAi: false,
          message: entry.question,
          isLoading: false,
        });
      }
      if (typeof entry.answer === "string" && entry.answer.length > 0) {
        result.push({
          uuid: `${id}-${entryId}-a`,
          isAi: true,
          message: entry.answer,
          isLoading: false,
        });
      }
      return result;
    });
    messages = mapped.length > 0 ? mapped : undefined;
  }

  return {
    id,
    idSession,
    title,
    timestamp,
    messages,
    url: "",
  };
};

const useLLM = (
  url: string,
  streamMode: boolean = false,
  startPrompt: boolean = false
) => {
  const translations = useTranslations();
  const llmInputRef = useRef<HTMLInputElement>(null);
  const modelInputRef = useRef<HTMLInputElement>(null);
  const [aiAnswers, setAiAnswers] = useState<AiMessage[]>([]);
  const [openAI, setOpenAI] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const organization = useSelectedOrganization();
  const organizationId = organization?.id || "";
  const user = useUser();
  const { sendSnack } = useSnackbar();

  // Abort Controller reference to stop execution
  const abortControllerRef = useRef<AbortController | null>(null);

  // Backend session ID — ref because it's read/written inside async callbacks
  const currentIdSessionRef = useRef<string | undefined>(undefined);

  // Reactive mirror of `currentIdSessionRef` so React components
  // (e.g. the share button) can re-render when the backend echoes the
  // id_session in its response header. The ref stays the source of
  // truth for async callbacks; this state only mirrors it for the UI.
  const [currentIdSession, setCurrentIdSession] = useState<string | undefined>(undefined);

  // Title for the current session, as returned by the backend in the
  // `Title` response header. Read in handleSubmit, consumed by the
  // session-list useEffect to surface the real title in the history tab.
  const currentTitleRef = useRef<string | null>(null);

  // Session states
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>("");

  // Model provider / model name. Set by <ModelProviderMenu /> when the
  // user activates the konami code "flowenti2". When unset, the POST
  // body is identical to the previous behavior (no provider/model
  // fields), so existing users are not affected.
  const [modelProvider, setModelProvider] = useState<
    "ollama" | "openrouter" | null
  >(null);
  const [modelName, setModelName] = useState<string>("");

  const startNewSession = () => {
    const newId = create_UUID();
    setCurrentSessionId(newId);
    currentIdSessionRef.current = undefined;
    setCurrentIdSession(undefined);
    // Reset the title carried over from the previous session: a new
    // session starts blank and the next response header `Title` will
    // repopulate it.
    currentTitleRef.current = null;
    setAiAnswers([]);
    if (startPrompt) {
      const welcomeMessage = getTranslation(translations, "ai_guide_welcome.message");
      setAiAnswers([
        {
          uuid: create_UUID(),
          isAi: true,
          message: welcomeMessage,
          isLoading: false,
        }
      ]);
    }
  };

  const loadSession = async (id: string) => {
    const session = sessions.find((s) => s.id === id);
    if (!session) return;

    // Already loaded? Use cached messages.
    if (session.messages && session.messages.length > 0) {
      setCurrentSessionId(id);
      currentIdSessionRef.current = session.idSession;
      setCurrentIdSession(session.idSession);
      setAiAnswers(session.messages);
      return;
    }

    // Fetch full session from backend
    if (!session.idSession || !user?.userId || !organizationId) {
      sendSnack({
        message: "Impossibile caricare la chat: identificativo mancante.",
        type: "error",
      });
      return;
    }
    try {
      const { data } = await aiApi.get<ChatSession | ChatSession[]>(
        `/sessions/${user.userId}/${organizationId}/${session.idSession}`
      );
      // Backend may return either a single ChatSession or a 1-element array
      const full = Array.isArray(data) ? data[0] : data;
      if (!full) return;
      const messages = full.messages ?? [];
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, messages } : s))
      );
      setCurrentSessionId(id);
      currentIdSessionRef.current = full.idSession ?? session.idSession;
      setCurrentIdSession(full.idSession ?? session.idSession);
      setAiAnswers(messages);
    } catch (e) {
      console.error("Error loading chat session", e);
      sendSnack({
        message: "Errore durante il caricamento della chat.",
        type: "error",
      });
    }
  };

  const deleteSession = async (id: string) => {
    const session = sessions.find((s) => s.id === id);
    if (!session?.idSession || !user?.userId || !organizationId) {
      sendSnack({
        message: "Impossibile eliminare la chat: identificativo mancante.",
        type: "error",
      });
      return;
    }
    try {
      await aiApi.delete(`/sessions/${user.userId}/${organizationId}/${session.idSession}`);
    } catch (e) {
      console.error("Error deleting chat session", e);
      sendSnack({
        message: "Errore durante l'eliminazione della chat.",
        type: "error",
      });
      return;
    }
    setSessions((prev) => prev.filter((s) => s.id !== id));
    if (currentSessionId === id) {
      startNewSession();
    }
  };

  const renameSession = async (id: string, newTitle: string) => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    const session = sessions.find((s) => s.id === id);
    if (!session?.idSession || !user?.userId || !organizationId) {
      sendSnack({
        message: "Impossibile rinominare la chat: identificativo mancante.",
        type: "error",
      });
      return;
    }
    try {
      await aiApi.patch(
        `/sessions/${user.userId}/${organizationId}/${session.idSession}/title`,
        { title: trimmed }
      );
    } catch (e) {
      console.error("Error renaming chat session", e);
      sendSnack({
        message: "Errore durante la rinominazione della chat.",
        type: "error",
      });
      return;
    }
    setSessions((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, title: trimmed, timestamp: Date.now() } : s
      )
    );
  };

  // Generate a public shareable link for a chat session by calling
  // `POST /ai/sessions/{user}/{org}/{session}/share` with body
  // `{ shared: true }`. The backend may return either a fully-qualified
  // URL or a relative slug; the latter is converted into an absolute
  // URL by `buildShareUrl` (defined in `common/shareUrl.ts` so it can
  // be reused by the public `/helpers/:slug` viewer). Mirrors the
  // error-handling pattern used by deleteSession / renameSession.
  const shareSession = async (
    id: string
  ): Promise<{ url: string } | null> => {
    const session = sessions.find((s) => s.id === id);
    // If the user shares a session from the history list we use its
    // persisted idSession; if they share the active chat that has not
    // yet been sent to the backend, we fall back to the live ref.
    const idSessionToShare =
      session?.idSession ?? currentIdSessionRef.current;
    if (!idSessionToShare || !user?.userId || !organizationId) {
      sendSnack({
        message: "Impossibile condividere la chat: identificativo mancante.",
        type: "error",
      });
      return null;
    }
    try {
      const { data } = await aiApi.post<
        { shared?: boolean; url?: string; link?: string; shareUrl?: string } | string
      >(
        `/sessions/${user.userId}/${organizationId}/${idSessionToShare}/share`,
        { shared: true }
      );
      const raw =
        typeof data === "string"
          ? data
          : data?.url ?? data?.shareUrl ?? data?.link ?? "";
      const finalUrl = buildShareUrl(raw);
      if (!finalUrl) {
        sendSnack({ message: "Link non disponibile.", type: "error" });
        return null;
      }
      return { url: finalUrl };
    } catch (e) {
      console.error("Error sharing chat session", e);
      sendSnack({
        message: "Errore durante la condivisione della chat.",
        type: "error",
      });
      return null;
    }
  };

  const [searching, setSearching] = useState(false);

  const searchSessions = async (query: string): Promise<ChatSession[]> => {
    if (!user?.userId || !organizationId) return [];
    const trimmed = query.trim();
    if (!trimmed) {
      // Empty query: return full list (no network)
      return sessions;
    }
    setSearching(true);
    try {
      const { data } = await aiApi.get<unknown>(
        `/sessions/${user.userId}/${organizationId}/search`,
        { params: { search: trimmed } }
      );
      const list = extractArray(data)
        .map(normalizeSession)
        .filter((s): s is ChatSession => s !== null)
        // The search endpoint may return rows with a generic/empty
        // title (e.g. "Chat" or the user's first question) before the
        // backend has finalised the auto-generated title. We always
        // prefer the title we already cached locally for that session
        // (which was set from the `Title` response header after the
        // first AI response), falling back to whatever the backend
        // returned if we have nothing better.
        .map((s) => {
          const cached = sessions.find((c) => c.id === s.id);
          const cachedTitle = cached?.title?.trim();
          const defaultNewChat =
            getTranslation(translations, "ai_drawer.new_chat_default_title") || "New chat";
          const backendTitleLooksGeneric =
            !s.title ||
            s.title === "Chat" ||
            s.title === defaultNewChat;
          const finalTitle =
            (cachedTitle && backendTitleLooksGeneric)
              ? cachedTitle
              : s.title || cachedTitle || defaultNewChat;
          return { ...s, title: finalTitle };
        });
      // Defensive sort: the search endpoint may not return rows in
      // timestamp order, so we sort here to match the loadSessions
      // ordering (most recent first).
      return list.sort((a, b) => b.timestamp - a.timestamp);
    } catch (e) {
      console.error("Error searching chat sessions", e);
      sendSnack({
        message: "Errore durante la ricerca delle chat.",
        type: "error",
      });
      return [];
    } finally {
      setSearching(false);
    }
  };

  const stopLLM = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
    setAiAnswers((p) => {
      // Remove the loading message if one is currently active
      if (p.length > 0 && p[p.length - 1].isLoading) {
        return p.slice(0, -1);
      }
      return p;
    });
  };

  // Load sessions from backend on mount (or when user/org becomes available)
  const loadSessionsFromBackend = useCallback(async () => {
    if (!user?.userId || !organizationId) return;
    try {
      const { data } = await aiApi.get<unknown>(
        `/sessions/${user.userId}/${organizationId}`
      );
      const list = extractArray(data)
        .map(normalizeSession)
        .filter((s): s is ChatSession => s !== null);
      if (list.length > 0) {
        setSessions(list);
      }
    } catch (e) {
      console.error("Error loading chat sessions", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, organizationId]);

  useEffect(() => {
    // Clear legacy localStorage data on first use
    try {
      localStorage.removeItem("flowenti_ai_sessions");
    } catch {
      // Ignore — localStorage may be disabled
    }
    // When the user switches organization (or logs in as a different
    // user), the in-memory chat must not bleed across orgs: the open
    // conversation, the current session id, the backend session id and
    // the cached title are all tied to a specific org. We clear them
    // synchronously *before* reloading the session list, so the UI
    // never shows a chat that belongs to the previous org with a list
    // populated by the new one. The `sessions` array is also wiped to
    // avoid flashing rows from the previous org while the new list is
    // still being fetched.
    if (user?.userId && organizationId) {
      setAiAnswers([]);
      setCurrentSessionId("");
      currentIdSessionRef.current = undefined;
      setCurrentIdSession(undefined);
      currentTitleRef.current = null;
      setSessions([]);
    }
    loadSessionsFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, organizationId]);

  // Update the in-memory session list whenever the conversation changes
  useEffect(() => {
    if (!currentSessionId) return;

    // Do not save empty sessions or sessions containing only the start prompt message
    const isOnlyWelcome = aiAnswers.length === 1 && aiAnswers[0].isAi && !aiAnswers[0].isLoading;
    if (aiAnswers.length === 0 || isOnlyWelcome) {
      return;
    }

    setSessions((prev) => {
      const existingIdx = prev.findIndex((s) => s.id === currentSessionId);
      // Title priority:
      //  1. The backend's `Title` response header (set in handleSubmit
      //     on first response) — the ONLY authoritative source. The
      //     backend is responsible for generating the title.
      //  2. The title already on the session — keep user-edited titles
      //     intact (avoid overwriting a manual rename).
      //  3. The default "New chat" placeholder for brand-new sessions
      //     that have no title yet.
      const existingTitle = existingIdx !== -1 ? prev[existingIdx].title : "";
      const backendTitle = currentTitleRef.current;
      const defaultNewChat = getTranslation(translations, "ai_drawer.new_chat_default_title") || "New chat";
      // Was the existing title set by something other than the user
      // (e.g. a previous backend `Title` header)? If so, prefer the
      // freshly-arrived header value. If it was set by the user via
      // rename, keep it untouched.
      const wasUserRenamed = existingTitle && existingTitle !== defaultNewChat
        && backendTitle && existingTitle !== backendTitle;
      const title = wasUserRenamed
        ? existingTitle
        : (backendTitle || existingTitle || defaultNewChat);

      const updatedSession: ChatSession = {
        id: currentSessionId,
        title,
        timestamp: Date.now(),
        messages: aiAnswers,
        url,
        idSession: currentIdSessionRef.current,
      };

      const newSessions = [...prev];
      if (existingIdx !== -1) {
        // Merge: keep all original fields, overwrite with the latest ones
        newSessions[existingIdx] = { ...newSessions[existingIdx], ...updatedSession };
      } else {
        newSessions.push(updatedSession);
      }
      return newSessions;
    });
  }, [aiAnswers, currentSessionId, url, translations]);

  const handleSubmit = async ({ ...extra }, hideUserInput: boolean = false, retryPrompt?: string) => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const prompt = retryPrompt !== undefined ? retryPrompt : (llmInputRef.current?.value || "");
      if (!prompt) return;

      if (llmInputRef.current && retryPrompt === undefined) {
        llmInputRef.current.value = "";
      }

      const aiUUID = create_UUID();

      if (retryPrompt === undefined) {
        setAiAnswers((p) => [
          ...p,
          {
            uuid: create_UUID(),
            isAi: false,
            message: prompt,
            hidden: hideUserInput,
            isLoading: false,
          },
          {
            uuid: aiUUID,
            isAi: true,
            message: "",
            isLoading: true,
            prompt,
            extra,
          },
        ]);
      } else {
        setAiAnswers((p) => [
          ...p,
          {
            uuid: aiUUID,
            isAi: true,
            message: "",
            isLoading: true,
            prompt,
            extra,
          },
        ]);
      }

      setLoading(true);

      // Always include `provider` and `model` in the request body, even
      // when the user has not picked them via the konami-triggered
      // provider menu. The backend is expected to treat empty strings
      // as "use the default provider/model" and not 500.
      const modelPayload: { provider: string; model: string } = {
        provider: modelProvider ?? "",
        model: modelName ?? "",
      };

      const result = await aiApi.post(
        url,
        {
          organizationId: organizationId,
          prompt: prompt,
          ...modelPayload,
          chat: aiAnswers
            .filter((m) => !m.isError) // exclude error messages from context
            .map(({ isAi, message }) => ({
              role: isAi ? "assistant" : "user",
              content: message,
            })),
          ...(currentIdSessionRef.current ? { id_session: currentIdSessionRef.current } : {}),
          // Echo the backend-generated `title` (received in the previous
          // response's `Title` header) on every subsequent request, so
          // the backend can persist it and the next
          // `GET /ai/sessions/{user}/{org}` returns it back to us. This
          // mirrors how `id_session` is bounced back: the client never
          // generates the title, it only relays what the backend told it.
          ...(currentTitleRef.current ? { title: currentTitleRef.current } : {}),
          ...extra,
        },
        {
          responseType: streamMode ? "stream" : "json",
          signal: controller.signal,
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.target.status >= 200 && progressEvent.target.status < 300) {
              // Extract id_session from response header (available from first chunk)
              const sessionId = progressEvent.target.getResponseHeader("id_session");
              if (sessionId && !currentIdSessionRef.current) {
                currentIdSessionRef.current = sessionId;
                setCurrentIdSession(sessionId);
              }
              // Extract Title from response header — the backend uses it
              // to communicate the auto-generated chat title. We persist
              // it in a ref so the session-list useEffect can surface it
              // in the history tab and in search results. Axios's
              // XMLHttpRequest under the hood normalises header names
              // case-insensitively, so we try both casings.
              const responseTitle =
                progressEvent.target.getResponseHeader("Title") ??
                progressEvent.target.getResponseHeader("title");
              if (responseTitle) {
                currentTitleRef.current = responseTitle;
              }

              const responseText = progressEvent.target.response;
              const isParsable = checkIfParsable(responseText);
              setAiAnswers((p) => {
                const list = [...p];
                const lastIdx = list.length - 1;
                if (lastIdx >= 0 && list[lastIdx].uuid === aiUUID) {
                  list[lastIdx] = {
                    ...list[lastIdx],
                    message: responseText,
                    parsing: !isParsable,
                    isLoading: false,
                  };
                }
                return list;
              });
            }
          },
        },
      );

      setLoading(false);
      abortControllerRef.current = null;

      // Extract id_session from response headers (works for both stream and non-stream)
      const sessionId = result.headers["id_session"];
      if (sessionId) {
        currentIdSessionRef.current = sessionId;
        setCurrentIdSession(sessionId);
      }
      // Same for Title (in non-streaming mode the header is on
      // result.headers directly, not in onDownloadProgress).
      const responseTitle =
        result.headers["Title"] ?? result.headers["title"];
      if (responseTitle) {
        currentTitleRef.current = responseTitle;
        // Apply the backend-generated title to the session in the
        // history list immediately, so the user sees it as soon as
        // the first response lands. We match by `idSession` first
        // (the backend id) and fall back to the local `id` (the
        // client UUID), because for new sessions `currentSessionId`
        // still holds the local UUID while `currentIdSessionRef`
        // holds the backend id_session that the response was
        // generated for.
        setSessions((prev) => {
          const backendId = currentIdSessionRef.current;
          const idx = prev.findIndex(
            (s) =>
              (backendId && s.idSession === backendId) ||
              s.id === currentSessionId
          );
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], title: responseTitle };
          return next;
        });
      }

      if (!streamMode) {
        const response = result.data.choices[0].message.content;
        setAiAnswers((p) => {
          const list = [...p];
          const lastIdx = list.length - 1;
          if (lastIdx >= 0 && list[lastIdx].uuid === aiUUID) {
            list[lastIdx] = {
              ...list[lastIdx],
              message: response,
              isLoading: false,
            };
          }
          return list;
        });
      }
    } catch (err: any) {
      if (axios.isCancel(err)) {
        console.log("LLM request cancelled");
        return;
      }
      setLoading(false);
      abortControllerRef.current = null;

      // Update loading bubble to be an error bubble
      setAiAnswers((p) => {
        const list = [...p];
        const lastIdx = list.length - 1;
        if (lastIdx >= 0 && list[lastIdx].isLoading) {
          list[lastIdx] = {
            ...list[lastIdx],
            isLoading: false,
            message: "Si è verificato un errore durante la generazione della risposta. Riprova.",
            isError: true,
          };
        }
        return list;
      });

      sendSnack({
        message: "Errore di connessione al provider AI.",
        type: "error",
      });
    }
  };

  const handleRetry = async (errorUuid: string) => {
    const errorMsg = aiAnswers.find((m) => m.uuid === errorUuid);
    if (!errorMsg) return;

    const prompt = errorMsg.prompt || "";
    const extra = errorMsg.extra || {};

    // Remove the error message from the list
    setAiAnswers((p) => p.filter((m) => m.uuid !== errorUuid));

    // Re-submit
    await handleSubmit(extra, errorMsg.hidden, prompt);
  };

  return {
    llmInputRef,
    aiAnswers,
    setAiAnswers,
    openAI,
    setOpenAI,
    handleSubmit,
    loading,
    modelInputRef,
    stopLLM,
    sessions,
    currentSessionId,
    currentIdSession,
    startNewSession,
    loadSession,
    deleteSession,
    renameSession,
    shareSession,
    handleRetry,
    searchSessions,
    searching,
    refreshSessions: loadSessionsFromBackend,
    modelProvider,
    setModelProvider,
    modelName,
    setModelName,
  };
};

export default useLLM;